import type { Comic } from '@tanko/shared'
import { db } from '../db/schema'
import { adoptCloudCategoryIds } from '../db/repo'
import { CloudApiClient } from './api-client'
import { catalogIdOf, loadCategories } from './category-service'
import { isRegistered } from './comic-sync'
import { errorText, type BulkOutcome } from './bulk-progress'
import {
  applyCategoryMigration, planCategoryMigration, remapAssignments,
  type CategoryMigrationEntry,
} from './category-migration'

const api = new CloudApiClient()
type MigrationClient = Pick<CloudApiClient, 'categories' | 'createCategory' | 'updateComic' | 'comic'>

/** 目前尚待遷移的本地分類，連同每一筆受影響的作品數。 */
export async function previewMigration(
  cloudCategories: readonly { id: string; name: string }[],
): Promise<CategoryMigrationEntry[]> {
  const [categories, assignments] = await Promise.all([
    db.categories.orderBy('sortOrder').toArray(),
    db.comicCategories.toArray(),
  ])
  const counts = new Map<string, number>()
  for (const assignment of assignments) {
    counts.set(assignment.categoryId, (counts.get(assignment.categoryId) ?? 0) + 1)
  }
  return planCategoryMigration(categories, cloudCategories, id => counts.get(id) ?? 0)
}

/** 本地分類是否已經全部對應到雲端識別碼——全部是 merge 就代表上一輪已經跑完。 */
export function migrationComplete(plan: readonly CategoryMigrationEntry[]): boolean {
  return plan.every(entry => entry.action !== 'create')
}

/**
 * 套用分類遷移。
 *
 * 順序固定為「建立雲端分類 → 改寫本地識別碼 → 同步鏡像 → 推送指派」。前三步之後
 * 本地與雲端使用同一組識別碼，因此中斷再跑一次時，既有分類會因為同名而判為合併，
 * 不會產生重複。最後一步逐本執行、遇錯即停。
 */
export async function runCategoryMigration(
  plan: readonly CategoryMigrationEntry[],
  client: MigrationClient = api,
  onProgress?: (completed: number, total: number) => void,
): Promise<BulkOutcome> {
  const completedIds: string[] = []
  let mapping: ReadonlyMap<string, string>
  try {
    const resolved = await applyCategoryMigration(plan, { createCategory: name => client.createCategory(name) })
    mapping = resolved.cloudIdByLocalId
    const assignments = await db.comicCategories.toArray()
    await adoptCloudCategoryIds(remapAssignments(assignments, mapping))
    await loadCategories(client)
  } catch (cause) {
    return { completed: 0, completedIds, reason: errorText(cause) }
  }

  // 指派推送只針對已註冊的作品：未註冊的在雲端沒有對應，等註冊完成後由整理流程帶上。
  const assignments = await db.comicCategories.toArray()
  const byComic = new Map<string, string[]>()
  for (const assignment of assignments) {
    byComic.set(assignment.comicId, [...(byComic.get(assignment.comicId) ?? []), assignment.categoryId])
  }
  const targets = (await db.comics.bulkGet([...byComic.keys()]))
    .filter((comic): comic is Comic => !!comic && isRegistered(comic))
  onProgress?.(0, targets.length)
  for (const comic of targets) {
    try {
      await client.updateComic(catalogIdOf(comic), { categoryIds: byComic.get(comic.id) ?? [] })
    } catch (cause) {
      return { completed: completedIds.length, completedIds, reason: errorText(cause) }
    }
    completedIds.push(comic.id)
    onProgress?.(completedIds.length, targets.length)
  }
  return { completed: completedIds.length, completedIds, reason: '' }
}
